import { defineComponent as _defineComponent } from 'vue'
import { computed, watch, onMounted } from 'vue'
    import { useId, useOrganizationUnitId, useRouter } from '@common/composables'
    import { useAdminPipelineDetailsPage } from '@Integrations/Pipelines/pages-core'

    
export default /*#__PURE__*/_defineComponent({
  __name: 'AdminPipelineDetailsPage',
  setup(__props) {

    const router = useRouter()
    const tid = useOrganizationUnitId()
    const id = useId()

    const {
        useCurrentUserQuery,
        usePipelineDetailsQuery,
        useAllPipelinesQuery,
        useActivatePipelineCommand,
        useDeactivatePipelineCommand,
        useTriggerPipelineManuallyCommand,
        useChangePipelineTimeTriggerRecurrencyCommand,
        useChangePipelineTriggerOnRunCompletedCommand,
        useChangePipelineInternalConnectorTriggerCommandV1,
        useChangeApplyChangesModeCommand,
        useSetPipelineLogLevelCommand,
        useResetPipelineRunsCommand,
        useDeletePipelineCommand,
        usePermanentDeletePipelineCommand,
    } = useAdminPipelineDetailsPage()

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)

    const { modelAllPipelinesQueryV1, runAllPipelinesQueryV1 } = useAllPipelinesQuery(tid)
    const pipelines = computed(() => modelAllPipelinesQueryV1.value?.Pipelines || [])
    const otherPipelines = computed(() => pipelines.value.filter(p => p.PipelineId != id))

    const { modelPipelineDetailsQueryV1, runPipelineDetailsQueryV1 } = usePipelineDetailsQuery(id)
    const name = computed(() => modelPipelineDetailsQueryV1.value?.Name)
    const isReadDirection = computed(() => modelPipelineDetailsQueryV1.value?.Direction === 1)
    const isWriteDirection = computed(() => modelPipelineDetailsQueryV1.value?.Direction === 2)
    const connectorName = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorProviderName)
    const datasetName = computed(() => modelPipelineDetailsQueryV1.value?.DatasetName)
    const isActive = computed(() => modelPipelineDetailsQueryV1.value?.IsActive)
    const connectorId = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorId)
    const datasetId = computed(() => modelPipelineDetailsQueryV1.value?.DatasetId)
    const isDeleted = computed(() => modelPipelineDetailsQueryV1.value?.IsDeleted)
    const connectorProviderReadPageUrl = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorProviderReadPageUrl)
    const connectorProviderWritePageUrl = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorProviderWritePageUrl)
    const connectorOnlyInternalTriggers = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorOnlyInternalTriggers)
    const connectorInternalTriggers = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorInternalTriggers ?? [])
    const multipleDataStreams = computed(() => modelPipelineDetailsQueryV1.value?.MultipleDataStreams ?? false)
    const multipleDataStreamsError = computed(() => modelPipelineDetailsQueryV1.value?.MultipleDataStreamsError)
    const dataStream = computed(() => modelPipelineDetailsQueryV1.value?.DataStream)
    const selectedConnectorInternalTrigger = computed(() => connectorInternalTriggers.value.find(t => t.Name == internalConnectorTrigger.value))
    const availableLogLevels = computed(() => modelPipelineDetailsQueryV1.value?.AvailableLogLevels ?? [])
    const {
        invokeActivatePipelineCommandV1,
        succeed: succeedActivatePipelineCommandV1
    } = useActivatePipelineCommand(id, tid, userId)

    const {
        invokeDeactivatePipelineCommandV1,
        succeed: succeedDeactivatePipelineCommandV1
    } = useDeactivatePipelineCommand(id, tid, userId)

    const {
        invokeTriggerPipelineManuallyCommandV1,
        succeedTriggerPipelineManuallyCommandV1
    } = useTriggerPipelineManuallyCommand(id, tid, userId)

    const {
        invokeChangePipelineTimeTriggerRecurrencyCommandV1,
        succeedChangePipelineTimeTriggerRecurrencyCommandV1,
        timeTriggerRecurrency
    } = useChangePipelineTimeTriggerRecurrencyCommand(id, tid, userId)

    const {
        invokeChangePipelineTriggerOnRunCompletedCommandV1,
        succeedChangePipelineTriggerOnRunCompletedCommandV1,
        triggerOnRunCompleted
    } = useChangePipelineTriggerOnRunCompletedCommand(id, tid, userId)

    const {
        invokeChangePipelineInternalConnectorTriggerCommandV1,
        succeedChangePipelineInternalConnectorTriggerCommandV1,
        internalConnectorTrigger
    } = useChangePipelineInternalConnectorTriggerCommandV1(id, tid, userId)

    const {
        invokeResetPipelineRunsCommandV1,
        succeedResetPipelineRunsCommandV1
    } = useResetPipelineRunsCommand(id, tid, userId)

    const {
        invokeChangeApplyChangesModeCommandV1,
        succeedChangeApplyChangesModeCommandV1,
        applyChangesMode
    } = useChangeApplyChangesModeCommand(id, tid, userId)

    const {
        invokeDeletePipelineCommandV1,
        succeedDeletePipelineCommandV1
    } = useDeletePipelineCommand(id, tid, userId)

    const {
        invokeSetPipelineLogLevelCommandV1,
        succeedSetPipelineLogLevelCommandV1,
        logLevel
    } = useSetPipelineLogLevelCommand(id, tid, userId)

    const {
        invokePermanentDeletePipelineCommandV1,
        succeedPermanentDeletePipelineCommandV1
    } = usePermanentDeletePipelineCommand(id)

    watch(name, () => document.title = name.value)

    watch(
        [succeedTriggerPipelineManuallyCommandV1, succeedPermanentDeletePipelineCommandV1],
        (val) => val ? router.push(`/admin/integrations/pipelines/dashboard/${tid}`) : null)

    watch(modelPipelineDetailsQueryV1, () => {
        timeTriggerRecurrency.value = modelPipelineDetailsQueryV1.value?.TimeTriggerRecurrency
        triggerOnRunCompleted.value = modelPipelineDetailsQueryV1.value?.TriggerOnRunCompleted
        internalConnectorTrigger.value = modelPipelineDetailsQueryV1.value?.InternalConnectorTrigger
        applyChangesMode.value = modelPipelineDetailsQueryV1.value?.ApplyChangesMode
        logLevel.value = modelPipelineDetailsQueryV1.value?.LogLevel
    })

    watch([
        succeedActivatePipelineCommandV1,
        succeedDeactivatePipelineCommandV1,
        succeedChangePipelineTimeTriggerRecurrencyCommandV1,
        succeedChangePipelineTriggerOnRunCompletedCommandV1,
        succeedChangePipelineInternalConnectorTriggerCommandV1,
        succeedResetPipelineRunsCommandV1,
        succeedDeletePipelineCommandV1
    ], () => runPipelineDetailsQueryV1())

    onMounted(() => {
        runCurrentUserQueryV1()
        runAllPipelinesQueryV1()
        runPipelineDetailsQueryV1()
    })

    function handleAbortPipelineRunsCommandV1() {
        if (confirm(`Do you really want to abort all runs of pipeline ${name.value}?`)) invokeResetPipelineRunsCommandV1()
    }

    function handleDeletePipelineCommandV1() {
        if (confirm(`Do you really want to delete pipeline ${name.value}?`)) invokeDeletePipelineCommandV1()
    }

    function handlePermanentDeletePipelineCommandV1() {
        if (confirm(`Do you really want to PERMANENTLY delete pipeline ${name.value}?`)) invokePermanentDeletePipelineCommandV1()
    }

return { __sfc: true,router, tid, id, useCurrentUserQuery, usePipelineDetailsQuery, useAllPipelinesQuery, useActivatePipelineCommand, useDeactivatePipelineCommand, useTriggerPipelineManuallyCommand, useChangePipelineTimeTriggerRecurrencyCommand, useChangePipelineTriggerOnRunCompletedCommand, useChangePipelineInternalConnectorTriggerCommandV1, useChangeApplyChangesModeCommand, useSetPipelineLogLevelCommand, useResetPipelineRunsCommand, useDeletePipelineCommand, usePermanentDeletePipelineCommand, modelCurrentUserQueryV1, runCurrentUserQueryV1, userId, modelAllPipelinesQueryV1, runAllPipelinesQueryV1, pipelines, otherPipelines, modelPipelineDetailsQueryV1, runPipelineDetailsQueryV1, name, isReadDirection, isWriteDirection, connectorName, datasetName, isActive, connectorId, datasetId, isDeleted, connectorProviderReadPageUrl, connectorProviderWritePageUrl, connectorOnlyInternalTriggers, connectorInternalTriggers, multipleDataStreams, multipleDataStreamsError, dataStream, selectedConnectorInternalTrigger, availableLogLevels, invokeActivatePipelineCommandV1, succeedActivatePipelineCommandV1, invokeDeactivatePipelineCommandV1, succeedDeactivatePipelineCommandV1, invokeTriggerPipelineManuallyCommandV1, succeedTriggerPipelineManuallyCommandV1, invokeChangePipelineTimeTriggerRecurrencyCommandV1, succeedChangePipelineTimeTriggerRecurrencyCommandV1, timeTriggerRecurrency, invokeChangePipelineTriggerOnRunCompletedCommandV1, succeedChangePipelineTriggerOnRunCompletedCommandV1, triggerOnRunCompleted, invokeChangePipelineInternalConnectorTriggerCommandV1, succeedChangePipelineInternalConnectorTriggerCommandV1, internalConnectorTrigger, invokeResetPipelineRunsCommandV1, succeedResetPipelineRunsCommandV1, invokeChangeApplyChangesModeCommandV1, succeedChangeApplyChangesModeCommandV1, applyChangesMode, invokeDeletePipelineCommandV1, succeedDeletePipelineCommandV1, invokeSetPipelineLogLevelCommandV1, succeedSetPipelineLogLevelCommandV1, logLevel, invokePermanentDeletePipelineCommandV1, succeedPermanentDeletePipelineCommandV1, handleAbortPipelineRunsCommandV1, handleDeletePipelineCommandV1, handlePermanentDeletePipelineCommandV1 }
}

})